import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

type FlashState = { successMessage?: string } | null

/**
 * Reads a one-off flash message passed via navigation state (e.g. after
 * creating a resource and redirecting to its list) and shows it once.
 *
 * It clears the history state immediately so the message does not reappear on
 * refresh or back navigation, and auto-dismisses after `autoHideMs`.
 */
export function useFlashMessage(autoHideMs = 5000) {
    const location = useLocation()
    const navigate = useNavigate()
    const [message, setMessage] = useState<string | null>(
        () => (location.state as FlashState)?.successMessage ?? null
    )

    // Consume the navigation state so a refresh/back does not show it again.
    // The replace below clears location.state, so this guard stops matching
    // and the effect becomes a no-op on subsequent runs.
    useEffect(() => {
        if ((location.state as FlashState)?.successMessage != null) {
            navigate(location.pathname, { replace: true, state: null })
        }
    }, [location.state, location.pathname, navigate])

    useEffect(() => {
        if (message == null) return
        const timer = setTimeout(() => setMessage(null), autoHideMs)
        return () => clearTimeout(timer)
    }, [message, autoHideMs])

    return message
}
